/**
 * 帳票作成 詳細（S-15・S-63・S-64・S-70・S-71 の詳細）。担当: G4
 * 明細の確認、特記事項の編集（実績報告書①②）、帳票出力（R-01/05/06/07/08/16）、削除。
 */
import { useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Download, Save, Trash2 } from 'lucide-react';
import { Button, DataTable, ErrorState, FormField, Loading, PageHeader, Panel, Textarea, useConfirm, useToast } from '@/components';
import { hasRole } from '@/auth/types';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { downloadFile } from '@/lib/download';
import { toWareki } from '@/lib/wareki';
import { useDeleteSnapshot, useSnapshot, useUpdateSnapshotHeader, type SnapshotDetail } from './api';
import type { FormTypeConfig } from './config';
import './forms.css';

export function SnapshotDetailPage({ config }: { config: FormTypeConfig }) {
  const { id } = useParams();
  const { data, isPending, error, refetch } = useSnapshot(Number(id));
  if (isPending) return <Loading block />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <Detail key={data.id} config={config} snap={data} />;
}

function Detail({ config, snap }: { config: FormTypeConfig; snap: SnapshotDetail }) {
  const user = useCurrentUser();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const { yearPath } = useFiscalYear();
  const update = useUpdateSnapshotHeader(snap.id);
  const remove = useDeleteSnapshot();
  const canEdit = hasRole(user, ['基金', '漁協']);
  const [notes, setNotes] = useState<Record<string, string>>(() =>
    Object.fromEntries(config.notes.map((n) => [n.key, String(snap.header[n.key] ?? '')])),
  );
  const [downloading, setDownloading] = useState<string | null>(null);

  const output = async (reportId: string, section?: string) => {
    setDownloading(reportId);
    try {
      const name = await downloadFile(`/report-snapshots/${snap.id}/report.xlsx`, { query: section ? { section } : undefined });
      toast.success('帳票を出力しました', name);
    } catch (e) {
      toast.error('帳票を出力できませんでした', errorMessage(e));
    } finally {
      setDownloading(null);
    }
  };

  const saveNotes = async () => {
    try {
      await update.mutateAsync(notes);
      toast.success('特記事項を更新しました');
    } catch (e) {
      toast.error('保存できませんでした', errorMessage(e));
    }
  };

  const onDelete = async () => {
    if (!(await confirm({ title: '帳票データを削除しますか？', message: '削除後に同じ条件で再作成できます。この操作は取り消せません。', tone: 'danger', confirmLabel: '削除する' }))) return;
    try {
      await remove.mutateAsync(snap.id);
      toast.success('帳票データを削除しました');
      navigate(yearPath(config.path));
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const cond = [
    { label: '事業実施者', value: snap.businessOperatorName },
    snap.baseDate ? { label: '基準日', value: `${snap.baseDate}（${toWareki(snap.baseDate)}）` } : null,
    snap.variant ? { label: config.createKind === 'dispatch' ? '四半期' : '区分', value: snap.variant } : null,
    snap.periodFrom || snap.periodTo ? { label: '期間', value: `${snap.periodFrom ?? ''} 〜 ${snap.periodTo ?? ''}` } : null,
    Array.isArray(snap.criteria.excludeVesselIds) && snap.criteria.excludeVesselIds.length
      ? { label: '除外船', value: `${snap.criteria.excludeVesselIds.length} 隻` }
      : null,
    { label: '作成日時', value: snap.createdAt.slice(0, 16).replace('T', ' ') },
    ...config.summary(snap.header),
  ].filter((x): x is { label: string; value: ReactNode } => x != null);

  return (
    <>
      <PageHeader
        eyebrow={config.title}
        title={`${snap.businessOperatorName}　${snap.variant ?? ''}`}
        meta={<span className="bs-forms-ids">{config.screenIds}</span>}
        actions={
          <>
            {canEdit && (
              <Button variant="danger" icon={<Trash2 size={16} />} loading={remove.isPending} onClick={() => void onDelete()}>
                削除
              </Button>
            )}
            {config.outputs.map((o) => (
              <Button key={o.reportId} variant="accent" icon={<Download size={16} />} loading={downloading === o.reportId} onClick={() => void output(o.reportId, o.section)}>
                {o.label}
              </Button>
            ))}
          </>
        }
      />
      <Panel title="作成条件">
        <dl className="bs-forms-dl">
          {cond.map((c) => (
            <div key={c.label} className="bs-forms-dl__row">
              <dt>{c.label}</dt>
              <dd>{c.value || '—'}</dd>
            </div>
          ))}
        </dl>
      </Panel>
      {config.notes.length > 0 && (
        <Panel
          title="特記事項"
          actions={
            canEdit && (
              <Button variant="primary" size="sm" icon={<Save size={15} />} loading={update.isPending} onClick={() => void saveNotes()}>
                保存する
              </Button>
            )
          }
        >
          <div className="bs-forms-notes">
            {config.notes.map((n) => (
              <FormField key={n.key} label={n.label}>
                <Textarea rows={3} maxLength={4000} value={notes[n.key] ?? ''} readOnly={!canEdit} onChange={(e) => setNotes({ ...notes, [n.key]: e.target.value })} />
              </FormField>
            ))}
          </div>
        </Panel>
      )}
      {config.sections.map((s) => (
        <section key={s.key} className={`bs-forms-section is-${config.createKind}`} aria-label={s.title}>
          <h2 className="bs-forms-h2">
            {s.title}
            <span className="bs-forms-h2__count bs-num">{(snap.sections[s.key] ?? []).length} 件</span>
          </h2>
          <div className="bs-forms-section__table">
            <DataTable
              caption={s.title}
              columns={s.columns}
              rows={snap.sections[s.key] ?? []}
              rowKey={(r) => String(r.no ?? r.tonClass ?? JSON.stringify(r))}
              dense
              pagination={{ pageSize: 50 }}
            />
          </div>
        </section>
      ))}
    </>
  );
}
